import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Trash2, Upload } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";

export default function PortfolioTab() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/photos").then((r) => setPhotos(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    const MAX_MB = 200;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (const file of files) {
      try {
        if (file.size > MAX_MB * 1024 * 1024) {
          toast.error(`${file.name}: exceeds the 200MB limit`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/admin/upload", fd, { timeout: 600000 });
        await api.post("/admin/photos", { url: up.data.url, categories: [], featured: false, hidden: false });
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${apiError(e)}`);
      } finally {
        setProgress((p) => ({ done: p.done + 1, total: p.total }));
      }
    }
    setUploading(false);
    setProgress(null);
    load();
    if (ok === 0) toast.error("Upload failed — please try again");
    else toast.success(`${ok} of ${files.length} photos uploaded`);
  };

  const addByUrl = async () => {
    if (!urlInput.trim()) return;
    try {
      await api.post("/admin/photos", { url: urlInput.trim(), categories: [], featured: false, hidden: false });
      setUrlInput("");
      load();
      toast.success("Photo added");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const update = async (id, patch) => {
    try {
      const r = await api.put(`/admin/photos/${id}`, patch);
      setPhotos((arr) => arr.map((p) => (p.id === id ? r.data : p)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/admin/photos/${id}`);
    load();
  };

  const move = async (photoId, dir) => {
    const visible = filterCat === "all" ? photos : photos.filter((p) => p.categories.includes(filterCat));
    const vi = visible.findIndex((p) => p.id === photoId);
    const neighbor = visible[vi + dir];
    if (vi < 0 || !neighbor) return;
    const arr = [...photos];
    const i = arr.findIndex((p) => p.id === photoId);
    const j = arr.findIndex((p) => p.id === neighbor.id);
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setPhotos(arr);
    await api.put("/admin/photos/reorder", { ordered_ids: arr.map((p) => p.id) });
  };

  const toggleCategory = (p, slug) => {
    const next = p.categories.includes(slug) ? p.categories.filter((c) => c !== slug) : [...p.categories, slug];
    update(p.id, { categories: next });
  };

  const [dragId, setDragId] = useState(null);
  const onDropPhoto = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const visible = filterCat === "all" ? photos : photos.filter((p) => p.categories.includes(filterCat));
    const target = visible.find((p) => p.id === targetId);
    if (!target) return;
    const arr = [...photos];
    const from = arr.findIndex((p) => p.id === dragId);
    const to = arr.findIndex((p) => p.id === targetId);
    arr.splice(to, 0, arr.splice(from, 1)[0]);
    setPhotos(arr);
    setDragId(null);
    await api.put("/admin/photos/reorder", { ordered_ids: arr.map((p) => p.id) });
    toast.success("Order saved");
  };

  return (
    <div data-testid="admin-portfolio">
      <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles([...e.dataTransfer.files]); }}
        onClick={() => fileRef.current?.click()}
        data-testid="portfolio-dropzone"
        className={`border-2 border-dashed p-10 text-center cursor-pointer mb-4 transition-colors ${dragging ? "border-[#1A1A1A] bg-white" : "border-[#E5E5E2] bg-white/50"}`}>
        <Upload size={20} strokeWidth={1.5} className="mx-auto mb-3 text-[#7A7A7A]" />
        <p className="text-sm text-[#4A4A4A]" data-testid="portfolio-upload-status">
          {uploading && progress ? `Uploading ${progress.done} of ${progress.total}…` : "Drag & drop photos here, or click to browse"}
        </p>
        <p className="text-xs text-[#7A7A7A] mt-1">JPG, PNG or WebP — up to 200MB each — select or drop as many as you like at once</p>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="portfolio-file-input"
          onChange={(e) => uploadFiles([...e.target.files])} />
      </div>
      <div className="flex justify-center mb-8">
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="portfolio-upload-btn"
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-3.5 hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photos"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">View & reorder by category:</label>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="portfolio-cat-filter"
          className="border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] bg-white">
          <option value="all">All photos</option>
          {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(filterCat === "all" ? photos : photos.filter((p) => p.categories.includes(filterCat))).map((p) => (
          <div key={p.id} data-testid={`admin-photo-${p.id}`} draggable
            onDragStart={() => setDragId(p.id)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDropPhoto(p.id)}
            className={`bg-white border p-3 cursor-grab active:cursor-grabbing ${dragId === p.id ? "opacity-40 border-[#1A1A1A]" : ""} ${p.hidden ? "border-[#E5E5E2] opacity-50" : "border-[#E5E5E2]"}`}>
            <p className="text-[9px] uppercase tracking-[0.15em] text-[#B5B5B0] mb-1.5">Drag to reorder</p>
            <img src={imgUrl(p.url)} alt={p.caption || "Portfolio"} className="w-full aspect-[4/3] object-cover mb-3" loading="lazy" />
            <input value={p.caption || ""} placeholder="Caption" onChange={(e) => update(p.id, { caption: e.target.value })}
              data-testid={`admin-photo-caption-${p.id}`}
              className="w-full border border-[#E5E5E2] px-2 py-1.5 text-xs mb-2 outline-none focus:border-[#1A1A1A]" />
            <div className="flex flex-wrap gap-1 mb-3">
              {categories.map((c) => (
                <button key={c.id} onClick={() => toggleCategory(p, c.slug)}
                  data-testid={`admin-photo-cat-${p.id}-${c.slug}`}
                  className={`text-[10px] px-2 py-1 border ${p.categories.includes(c.slug) ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5E2] text-[#4A4A4A]"}`}>
                  {c.name}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex gap-2">
                <button onClick={() => update(p.id, { featured: !p.featured })} data-testid={`admin-photo-featured-${p.id}`}
                  className={`px-2 py-1 border ${p.featured ? "bg-amber-50 border-amber-300 text-amber-700" : "border-[#E5E5E2] text-[#7A7A7A]"}`}>
                  {p.featured ? "Featured" : "Feature"}
                </button>
                <button onClick={() => update(p.id, { hidden: !p.hidden })} data-testid={`admin-photo-hide-${p.id}`}
                  className="px-2 py-1 border border-[#E5E5E2] text-[#7A7A7A]">
                  {p.hidden ? "Show" : "Hide"}
                </button>
              </div>
              <div className="flex gap-1">
                <button onClick={() => move(p.id, -1)} data-testid={`admin-photo-left-${p.id}`} className="p-1.5 border border-[#E5E5E2]"><ArrowLeft size={12} /></button>
                <button onClick={() => move(p.id, 1)} data-testid={`admin-photo-right-${p.id}`} className="p-1.5 border border-[#E5E5E2]"><ArrowRight size={12} /></button>
                <button onClick={() => remove(p.id)} data-testid={`admin-photo-delete-${p.id}`} className="p-1.5 border border-[#E5E5E2] text-red-500"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {photos.length === 0 && <p className="text-sm text-[#7A7A7A] text-center py-10">No photos yet — upload your first.</p>}
    </div>
  );
}
